import {
  ComboboxPopover,
  type Option,
} from '@/components/ui/combobox/combobox-popover';
import { pdkAxios } from '@/lib/pdk-axios';
import { tradeStore } from '@/models/stores';
import type { Trade, TradeCurrentLocation } from '@/models/types';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import TradeCurrentLocationBadge from './trade-current-location-badge';

export const CURRENT_LOCATION_OPTIONS: Option<TradeCurrentLocation>[] = [
  {
    label: 'Customer',
    value: 'customer',
    hotkey: '1',
  },
  {
    label: 'Transit',
    value: 'transit',
    hotkey: '2',
  },
  {
    label: 'HQ',
    value: 'hq',
    hotkey: '3',
  },
  {
    label: 'Auction',
    value: 'auction',
    hotkey: '4',
  },
];

type TradeCurrentLocationEditorProps = {
  tradeId: Trade['id'];
  currentLocation: TradeCurrentLocation;
};

export default function TradeCurrentLocationEditor({
  tradeId,
  currentLocation,
}: TradeCurrentLocationEditorProps) {
  const [open, setOpen] = useState(false);

  const currentOption = useMemo(() => {
    return CURRENT_LOCATION_OPTIONS.find(
      (option) => option.value === currentLocation,
    );
  }, [currentLocation]);

  const updateCurrentLocationMutation = useMutation({
    onMutate: async ({
      currentLocation,
    }: {
      currentLocation: TradeCurrentLocation;
    }) => {
      tradeStore.update({
        id: tradeId,
        trade: {
          current_location: currentLocation,
        },
      });
    },
    mutationFn: async ({
      currentLocation,
    }: {
      currentLocation: TradeCurrentLocation;
    }) => {
      const response = await pdkAxios.post(
        `/trades/${tradeId}/current_location`,
        {
          current_location: currentLocation,
        },
      );

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error updating current location', error, variables);
    },
  });

  const handleCurrentLocationChange = (
    newCurrentLocation: TradeCurrentLocation,
  ) => {
    updateCurrentLocationMutation.mutate({
      currentLocation: newCurrentLocation,
    });
    setOpen(false);
  };

  return (
    <div className="flex flex-row gap-1 items-center justify-between">
      <div className="flex flex-row gap-1 items-center">
        <div className="text-sm text-gray-500 dark:text-white">
          Current Location
        </div>
      </div>
      <ComboboxPopover
        open={open}
        onOpenChange={setOpen}
        endAdornment={null}
        placeholder="Select location..."
        onChange={handleCurrentLocationChange}
        emptyMessage="No location found"
        currentOption={currentOption}
        options={CURRENT_LOCATION_OPTIONS}
        align="end"
        onKeyDown={(e) => {
          const keyPressed = e.key;

          const option = CURRENT_LOCATION_OPTIONS.find(
            (option) => option.hotkey === keyPressed,
          );

          if (option) {
            handleCurrentLocationChange(option.value);
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
          }
        }}
        stayOpenAfterSelection={false}
      >
        <div>
          <TradeCurrentLocationBadge currentLocation={currentLocation} />
        </div>
      </ComboboxPopover>
    </div>
  );
}
